/**
 * Model opaski — jedyne miejsce, w ktorym front tlumaczy odpowiedzi backendu na
 * pojecie uzywane przez ekrany.
 *
 * Backend liczy klasyfikacje jednym wyrazeniem, ale wystawia ja pod dwiema nazwami
 * (kontrakt potwierdzony przez backend 2026-08-20):
 *
 *   GET /manager/getBandType/{imei}          -> 'REVJ' | 'CARE' | 'ONE' | 'EASY_CARE'
 *   pole `type` w /patient/my, /myShortInfo
 *   oraz /patient/{identifier}               -> 'VITAL CARE' | 'CARE' | 'ONE' | 'EASY_CARE'
 *
 * Rozni je WYLACZNIE nazwa RevJ. Stad dwa wejscia ponizej i jedna odpowiedz.
 *
 * Pozostale pola o typie opaski to surowe kolumny bez logiki:
 *   `bandType` (band.type_hardware)  — dla opasek RevJ ma wartosc CARE, wiec o modelu
 *                                      nie mowi; przy pustej kolumnie raz null, raz ''.
 *                                      Czytamy z niego JEDEN kod — patrz listRowCode()
 *   `dev` (band.band_type)           — istnieje tylko w /patient/my; w /{identifier}
 *                                      zawsze null, w /myShortInfo nie ma go wcale
 *   `type` z patient-edit-initial-data — to RODZINA sprzetowa (bandFamily), nie model
 *
 * Planner nie ma wlasnego kodu w zadnym z nich — poznaje sie go po prefiksie IMEI.
 */
export type BandModel = 'PLANNER' | 'VITAL_CARE' | 'CARE_PRO' | 'ONE' | 'EASY_CARE' | 'UNKNOWN';

/** Kod, ktorym backend ma opisywac SiDLY Easy Care. Do zmiany w JEDNYM miejscu. */
export const BAND_EASY_CARE = 'EASY_CARE';

const PLANNER_IMEI_PREFIX = '644';

/** Wartosci z GET /manager/getBandType. */
const FROM_CLASSIFIED: { [code: string]: BandModel } = {
  REVJ: 'VITAL_CARE',
  CARE: 'CARE_PRO',
  ONE: 'ONE',
  [BAND_EASY_CARE]: 'EASY_CARE',
};

/**
 * Pole `type` z list pacjentow i z /patient/{identifier}. To rowniez KLASYFIKACJA,
 * liczona tym samym wyrazeniem co getBandType — rozni sie wylacznie nazwa RevJ:
 * tam "REVJ", tu "VITAL CARE" (ze spacja).
 *
 * Pola `dev` z wiersza listy nie czytamy wcale: istnieje tylko w /patient/my,
 * w /patient/{identifier} jest zawsze null, a w /patient/myShortInfo nie ma go wcale.
 */
const FROM_LIST_ROW: { [code: string]: BandModel } = {
  'VITAL CARE': 'VITAL_CARE',
  CARE: 'CARE_PRO',
  ONE: 'ONE',
  [BAND_EASY_CARE]: 'EASY_CARE',
};

function isPlannerImei(imei: string | number): boolean {
  return imei != null && String(imei).startsWith(PLANNER_IMEI_PREFIX);
}

function lookUp(table: { [code: string]: BandModel }, code: string, imei: string | number): BandModel {
  if (isPlannerImei(imei)) {
    return 'PLANNER';
  }
  return table[code] || 'UNKNOWN';
}

/**
 * Kod modelu z wiersza listy: klasyfikacja z `type`, a gdy jej nie ma — surowe `bandType`.
 *
 * Backend liczy `type` przez `AppBundle\Band\BandType` (kontrakt potwierdzony
 * 2026-09-01) — Easy Care przychodzi tam już poprawnie jako `EASY_CARE`, bez
 * osobnego wyjatku tutaj. Wczesniej `type` mial wlasna, sprzed-Easy-Care kopie tej
 * reguly (`AppBundle\Patient\Assembler::productType`), ktora kazda opaske spoza
 * RevJ i CARE wrzucala do ONE — stad ten front musial podmieniac wynik reczie,
 * czytajac `bandType` (surowe `type_hardware`, ktore i tak dla Easy Care rzadko
 * cokolwiek niesie). Backend to naprawil u zrodla; front juz nie duplikuje reguly.
 */
function listRowCode(row: any): string {
  return row.type || row.bandType;
}

/** Ekrany szczegolowe: odpowiedz GET /manager/getBandType plus IMEI pacjenta. */
export function bandModelFromApi(bandType: string, imei: string | number): BandModel {
  return lookUp(FROM_CLASSIFIED, bandType, imei);
}

/**
 * Listy pacjentow i szczegoly pacjenta: pole `type` (klasyfikacja, nie surowa kolumna).
 *
 * Gdy wiersz nie niesie `type`, spadamy na surowe `bandType` (type_hardware). Jest grubsze,
 * bo nie odroznia Vital Care od Care Pro — obie maja tam CARE — ale do stanu opaski to
 * wystarcza i dokladnie tak liczyla je lista, zanim rozpoznawanie modelu trafilo tutaj.
 */
export function bandModelFromListRow(user: any): BandModel {
  const row = user || {};
  return lookUp(FROM_LIST_ROW, listRowCode(row), row.imei);
}

/**
 * Czy opaska przysyla pomiary zyciowe (puls, saturacja, oddech, sen, temperatura).
 *
 * To jest pytanie, ktore naprawde zadaja ekrany — nie "czy to Easy Care". Dzieki temu
 * kolejny model bez czujnika optycznego wymaga zmiany TU, a nie w kilkunastu warunkach.
 *
 * UNKNOWN celowo odpowiada `true`: nierozpoznany model to najczesciej opaska, ktorej
 * typu nie udalo sie odczytac (brak wiersza w register_imei -> HTTP 400, timeout,
 * odpowiedz jeszcze w drodze). Nieudany odczyt nie moze udawac wlasciwosci urzadzenia,
 * wiec zostawiamy dotychczasowy, bogatszy interfejs.
 */
export function measuresVitals(model: BandModel): boolean {
  return model !== 'EASY_CARE';
}

/**
 * Czy przy tym modelu pokazujemy status zalozona/zdjeta.
 *
 * Dla Easy Care to decyzja produktowa, a nie brak danych: sprzetowo jest Vitalem,
 * wiec zalozenie wykrywa i backend ma z niej `is_wear` oraz powiadomienia band_worn.
 * ONE nie ma tego czym zmierzyc.
 *
 * UNKNOWN odpowiada `true` z tego samego powodu co w measuresVitals: nieudany odczyt
 * typu nie moze odbierac statusu opasce, ktora go ma.
 */
export function showsWearStatus(model: BandModel): boolean {
  return model !== 'ONE' && model !== 'EASY_CARE';
}
